import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Pressable,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  Image,
  Dimensions,
  TextInput as RNTextInput,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Text from '../../components/common/Text';
import { RootStackParamList } from '../../types/navigation';
import { useAuth } from '../../hooks/useAuth';
import TextField from '../../components/common/TextField';
import Button from '../../components/common/Button';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import ResponsiveContainer from '../../components/shared/ResponsiveContainer';
import EyeIcon from '../../components/common/EyeIcon';
import { useTheme } from '../../hooks/useTheme';
import { trackButtonClick } from '../../utils/analytics';
import { shouldNavigateToVerifyEmailAfterSignUp } from '../../utils/authSignInSignUp';

// Form validation schema
const registerSchema = z
  .object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z
      .string()
      .min(6, 'Password must be at least 6 characters'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type RegisterFormData = z.infer<typeof registerSchema>;

type RegisterScreenRouteProp = RouteProp<RootStackParamList, 'Register'>;

const RegisterScreen = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute<RegisterScreenRouteProp>();
  const { signUp } = useAuth();
  const { isMobile, screenWidth } = useResponsiveLayout();
  const { colors } = useTheme();
  const [signupError, setSignupError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const emailInputRef = useRef<RNTextInput>(null);

  const inviteCode = route.params?.inviteCode;

  // Focus email input when component mounts
  useEffect(() => {
    const focusEmail = () => {
      if (emailInputRef.current) {
        emailInputRef.current.focus();
      } else {
        // Retry if ref is not ready yet
        setTimeout(focusEmail, 50);
      }
    };

    const timer = setTimeout(focusEmail, 200);
    return () => clearTimeout(timer);
  }, []);

  const isTablet = screenWidth >= 768 && screenWidth < 1024;
  const logoSize = isMobile ? 40 : isTablet ? 50 : 60;
  const formPadding = isMobile ? 24 : isTablet ? 40 : 50;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: 'onBlur', // Only validate on blur, not on change
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (data: RegisterFormData) => {
    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }

    trackButtonClick('Create Account', 'register-screen', {
      button_type: 'create_account',
      screen: 'register',
      has_invite_code: !!inviteCode,
    });

    try {
      setIsSubmitting(true);
      setSignupError(null);
      const result = await signUp(
        data.email,
        data.password,
        undefined,
        inviteCode
      );
      if (shouldNavigateToVerifyEmailAfterSignUp(result)) {
        navigation.navigate('VerifyEmailRequired');
      }
    } catch (error: unknown) {
      setSignupError(
        error instanceof Error
          ? error.message
          : 'Registration failed. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Animation useEffect - commented out temporarily
  // useEffect(() => {
  //   const animations = [
  //     bubbleAnimation1, bubbleAnimation2, bubbleAnimation3, bubbleAnimation4, bubbleAnimation5,
  //     bubbleAnimation6, bubbleAnimation7, bubbleAnimation8, bubbleAnimation9, bubbleAnimation10,
  //     bubbleAnimation11, bubbleAnimation12, bubbleAnimation13, bubbleAnimation14, bubbleAnimation15,
  //     bubbleAnimation16, bubbleAnimation17, bubbleAnimation18, bubbleAnimation19, bubbleAnimation20
  //   ];

  //   animations.forEach((animation, index) => {
  //     Animated.loop(
  //       Animated.sequence([
  //         Animated.timing(animation, {
  //           toValue: 1,
  //           duration: 3000 + (index * 200),
  //           useNativeDriver: Platform.OS !== 'web',
  //         }),
  //         Animated.timing(animation, {
  //           toValue: 0,
  //           duration: 3000 + (index * 200),
  //           useNativeDriver: Platform.OS !== 'web',
  //         }),
  //       ])
  //     ).start();
  //   });
  // }, []);

  return (
    <ResponsiveContainer
      style={[styles.container, { backgroundColor: colors.authBackground }]}
    >
      {/* Animated Background Bubbles - commented out temporarily */}
      {/* <View style={styles.bubblesContainer}>
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble1,
            {
              transform: [
                {
                  translateY: bubbleAnimation1.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -50],
                  }),
                },
                {
                  scale: bubbleAnimation1.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.8, 1.2, 0.8],
                  }),
                },
              ],
              opacity: bubbleAnimation1.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.3, 0.7, 0.3],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble2,
            {
              transform: [
                {
                  translateY: bubbleAnimation2.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -60],
                  }),
                },
                {
                  scale: bubbleAnimation2.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.6, 1.0, 0.6],
                  }),
                },
              ],
              opacity: bubbleAnimation2.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.3, 0.7, 0.3],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble3,
            {
              transform: [
                {
                  translateY: bubbleAnimation3.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -60],
                  }),
                },
                {
                  scale: bubbleAnimation3.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.7, 1.1, 0.7],
                  }),
                },
              ],
              opacity: bubbleAnimation3.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.25, 0.6, 0.25],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble4,
            {
              transform: [
                {
                  translateY: bubbleAnimation4.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -70],
                  }),
                },
                {
                  scale: bubbleAnimation4.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.5, 0.9, 0.5],
                  }),
                },
              ],
              opacity: bubbleAnimation4.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.2, 0.5, 0.2],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble5,
            {
              transform: [
                {
                  translateY: bubbleAnimation5.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -40],
                  }),
                },
                {
                  scale: bubbleAnimation5.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.9, 1.3, 0.9],
                  }),
                },
              ],
              opacity: bubbleAnimation5.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.3, 0.6, 0.3],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble6,
            {
              transform: [
                {
                  translateY: bubbleAnimation6.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -80],
                  }),
                },
                {
                  scale: bubbleAnimation6.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.4, 0.8, 0.4],
                  }),
                },
              ],
              opacity: bubbleAnimation6.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.2, 0.5, 0.2],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble7,
            {
              transform: [
                {
                  translateY: bubbleAnimation7.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -45],
                  }),
                },
                {
                  scale: bubbleAnimation7.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.7, 1.1, 0.7],
                  }),
                },
              ],
              opacity: bubbleAnimation7.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.25, 0.6, 0.25],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble8,
            {
              transform: [
                {
                  translateY: bubbleAnimation8.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -55],
                  }),
                },
                {
                  scale: bubbleAnimation8.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.6, 1.0, 0.6],
                  }),
                },
              ],
              opacity: bubbleAnimation8.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.3, 0.7, 0.3],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble9,
            {
              transform: [
                {
                  translateY: bubbleAnimation9.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -65],
                  }),
                },
                {
                  scale: bubbleAnimation9.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.5, 0.9, 0.5],
                  }),
                },
              ],
              opacity: bubbleAnimation9.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.2, 0.5, 0.2],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble10,
            {
              transform: [
                {
                  translateY: bubbleAnimation10.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -35],
                  }),
                },
                {
                  scale: bubbleAnimation10.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.8, 1.2, 0.8],
                  }),
                },
              ],
              opacity: bubbleAnimation10.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.3, 0.7, 0.3],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble11,
            {
              transform: [
                {
                  translateY: bubbleAnimation11.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -75],
                  }),
                },
                {
                  scale: bubbleAnimation11.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.4, 0.8, 0.4],
                  }),
                },
              ],
              opacity: bubbleAnimation11.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.2, 0.5, 0.2],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble12,
            {
              transform: [
                {
                  translateY: bubbleAnimation12.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -50],
                  }),
                },
                {
                  scale: bubbleAnimation12.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.7, 1.1, 0.7],
                  }),
                },
              ],
              opacity: bubbleAnimation12.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.25, 0.6, 0.25],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble13,
            {
              transform: [
                {
                  translateY: bubbleAnimation13.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -60],
                  }),
                },
                {
                  scale: bubbleAnimation13.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.6, 1.0, 0.6],
                  }),
                },
              ],
              opacity: bubbleAnimation13.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.3, 0.7, 0.3],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble14,
            {
              transform: [
                {
                  translateY: bubbleAnimation14.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -40],
                  }),
                },
                {
                  scale: bubbleAnimation14.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.9, 1.3, 0.9],
                  }),
                },
              ],
              opacity: bubbleAnimation14.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.3, 0.6, 0.3],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble15,
            {
              transform: [
                {
                  translateY: bubbleAnimation15.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -70],
                  }),
                },
                {
                  scale: bubbleAnimation15.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.5, 0.9, 0.5],
                  }),
                },
              ],
              opacity: bubbleAnimation15.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.2, 0.5, 0.2],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble16,
            {
              transform: [
                {
                  translateY: bubbleAnimation16.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -45],
                  }),
                },
                {
                  scale: bubbleAnimation16.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.7, 1.1, 0.7],
                  }),
                },
              ],
              opacity: bubbleAnimation16.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.25, 0.6, 0.25],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble17,
            {
              transform: [
                {
                  translateY: bubbleAnimation17.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -55],
                  }),
                },
                {
                  scale: bubbleAnimation17.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.6, 1.0, 0.6],
                  }),
                },
              ],
              opacity: bubbleAnimation17.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.3, 0.7, 0.3],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble18,
            {
              transform: [
                {
                  translateY: bubbleAnimation18.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -65],
                  }),
                },
                {
                  scale: bubbleAnimation18.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.5, 0.9, 0.5],
                  }),
                },
              ],
              opacity: bubbleAnimation18.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.2, 0.5, 0.2],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble19,
            {
              transform: [
                {
                  translateY: bubbleAnimation19.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -35],
                  }),
                },
                {
                  scale: bubbleAnimation19.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.8, 1.2, 0.8],
                  }),
                },
              ],
              opacity: bubbleAnimation19.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.3, 0.7, 0.3],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble20,
            {
              transform: [
                {
                  translateY: bubbleAnimation20.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -75],
                  }),
                },
                {
                  scale: bubbleAnimation20.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.4, 0.8, 0.4],
                  }),
                },
              ],
              opacity: bubbleAnimation20.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.2, 0.5, 0.2],
              }),
            },
          ]}
        />
      </View> */}

      <KeyboardAvoidingView
        style={styles.keyboardAvoidingView}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={0}
      >
        <ScrollView
          contentContainerStyle={[
            styles.scrollContentContainer,
            isMobile && styles.mobileScrollContent,
          ]}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          bounces={isMobile}
        >
          <View
            style={[
              styles.contentContainer,
              !isMobile && styles.webContentContainer,
              isMobile && styles.mobileContentWrapper,
            ]}
          >
            <View
              style={[
                styles.formContainer,
                {
                  backgroundColor: colors.card,
                  shadowColor: colors.border,
                  borderColor: colors.borderLight,
                  paddingHorizontal: formPadding,
                },
              ]}
            >
              {/* Form Header */}
              <View style={styles.formHeader}>
                <Pressable
                  onPress={() => navigation.navigate('Landing')}
                  style={({ focused }) => [
                    styles.logoContainer,
                    Platform.OS === 'web' &&
                      focused && {
                        outlineWidth: 2,
                        outlineStyle: 'solid',
                        outlineColor: colors.theme_color || colors.primary,
                        outlineOffset: 2,
                      },
                  ]}
                >
                  <Image
                    source={require('../../../assets/logo-square.png')}
                    style={{ width: logoSize, height: logoSize }}
                    resizeMode="contain"
                  />
                </Pressable>
                <Text
                  style={[
                    styles.formTitle,
                    {
                      color: colors.primary,
                      fontSize: 16,
                      fontWeight: '600',
                    },
                  ]}
                >
                  Create Account
                </Text>
                <Text
                  style={[
                    styles.formSubtitle,
                    {
                      color: colors.secondary,
                      fontSize: isMobile ? 14 : 17,
                    },
                  ]}
                >
                  Sign up for your ColourSense account
                </Text>
              </View>

              <View style={styles.form}>
                <Controller
                  control={control}
                  name="email"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <TextField
                      ref={emailInputRef}
                      label="Email Address"
                      placeholder="Enter your email address"
                      keyboardType="email-address"
                      autoCapitalize="none"
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={errors.email?.message}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="password"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <TextField
                      label="Password"
                      placeholder="Create a password"
                      secureTextEntry={!showPassword}
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={errors.password?.message}
                      rightIcon={<EyeIcon isVisible={showPassword} />}
                      onRightIconPress={() => setShowPassword(!showPassword)}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="confirmPassword"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <TextField
                      label="Confirm Password"
                      placeholder="Confirm your password"
                      secureTextEntry={!showConfirmPassword}
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={errors.confirmPassword?.message}
                      rightIcon={<EyeIcon isVisible={showConfirmPassword} />}
                      onRightIconPress={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    />
                  )}
                />
              </View>

              {signupError && (
                <View
                  style={[
                    styles.errorContainer,
                    { backgroundColor: colors.errorBackground },
                  ]}
                >
                  <Text style={[styles.errorText, { color: colors.errorText }]}>
                    {signupError}
                  </Text>
                </View>
              )}

              <View style={styles.agreementContainer}>
                <Text
                  style={[
                    styles.legalText,
                    {
                      color: colors.secondary,
                      fontSize: isMobile ? 10 : 13,
                    },
                    styles.introText,
                  ]}
                >
                  By signing up, you agree to our
                </Text>
                <View style={styles.linksContainer}>
                  <Pressable
                    onPress={() => navigation.navigate('TermsOfService')}
                    hitSlop={{ top: 10, bottom: 10, left: 5, right: 5 }}
                    style={({ focused }) =>
                      Platform.OS === 'web' && focused
                        ? {
                            outlineWidth: 2,
                            outlineStyle: 'solid',
                            outlineColor: colors.theme_color || colors.primary,
                            outlineOffset: 2,
                            borderRadius: 4,
                          }
                        : {}
                    }
                  >
                    <Text
                      style={[
                        styles.legalText,
                        {
                          color: colors.primary,
                          fontSize: isMobile ? 10 : 13,
                        },
                      ]}
                    >
                      Terms of Use
                    </Text>
                  </Pressable>
                  <View style={styles.separatorContainer}>
                    <Text
                      style={[
                        styles.legalText,
                        {
                          color: colors.secondary,
                          fontSize: isMobile ? 10 : 13,
                        },
                      ]}
                    >
                      {' | '}
                    </Text>
                  </View>
                  <Pressable
                    onPress={() => navigation.navigate('PrivacyPolicy')}
                    hitSlop={{ top: 10, bottom: 10, left: 5, right: 5 }}
                    style={({ focused }) =>
                      Platform.OS === 'web' && focused
                        ? {
                            outlineWidth: 2,
                            outlineStyle: 'solid',
                            outlineColor: colors.theme_color || colors.primary,
                            outlineOffset: 2,
                            borderRadius: 4,
                          }
                        : {}
                    }
                  >
                    <Text
                      style={[
                        styles.legalText,
                        {
                          color: colors.primary,
                          fontSize: isMobile ? 10 : 13,
                        },
                      ]}
                    >
                      Privacy Policy
                    </Text>
                  </Pressable>
                </View>
              </View>

              <Button
                title="Create Account"
                onPress={handleSubmit(onSubmit)}
                loading={isSubmitting}
                style={styles.createAccountButton}
                trackClick={true}
                trackingLocation="register-screen"
                trackingData={{
                  button_name: 'Create Account',
                  button_type: 'create_account',
                  screen: 'register',
                }}
              />

              <View style={styles.footer}>
                <Text
                  style={[
                    styles.footerText,
                    { color: colors.secondary, fontSize: isMobile ? 14 : 17 },
                  ]}
                >
                  Already have an account?{' '}
                </Text>
                <Pressable
                  onPress={() => navigation.navigate('Login')}
                  style={({ focused }) =>
                    Platform.OS === 'web' && focused
                      ? {
                          outlineWidth: 2,
                          outlineStyle: 'solid',
                          outlineColor: colors.theme_color || colors.primary,
                          outlineOffset: 2,
                          borderRadius: 6,
                        }
                      : {}
                  }
                >
                  <Text
                    style={[
                      styles.footerLink,
                      { color: colors.primary, fontSize: isMobile ? 14 : 17 },
                    ]}
                  >
                    Sign in
                  </Text>
                </Pressable>
              </View>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </ResponsiveContainer>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContentContainer: {
    flexGrow: 1,
    justifyContent: 'center',
    minHeight: Platform.OS === 'web' ? '100%' : Dimensions.get('window').height,
  },
  mobileScrollContent: {
    width: '100%',
    minHeight: Dimensions.get('window').height,
  },
  mobileContentWrapper: {
    width: '100%',
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 24,
  },
  contentContainer: {
    flex: 1,
    padding: 20,
    justifyContent: 'center',
  },
  formContainer: {
    borderRadius: Platform.OS === 'web' ? 20 : 24,
    paddingHorizontal: Platform.OS === 'web' ? 50 : 24,
    paddingVertical: Platform.OS === 'web' ? 35 : 32,
    minHeight: Platform.OS === 'web' ? 320 : undefined,
    shadowOffset: {
      width: 0,
      height: 12,
    },
    shadowOpacity: 0.3,
    shadowRadius: 25,
    elevation: 12,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  formHeader: {
    marginBottom: 12,
    alignItems: 'center',
  },
  logoContainer: {
    marginBottom: 8,
    padding: 8,
    borderRadius: 12,
    ...(Platform.OS === 'web' && {
      outlineWidth: 0,
      outlineColor: 'transparent',
      outlineStyle: 'solid',
    }),
  },
  formTitle: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
    fontSize: 16,
    fontWeight: '800',
    textAlign: 'center',
    marginTop: 4,
    marginBottom: 4,
    letterSpacing: -0.8,
    textShadowColor: 'rgba(0, 0, 0, 0.1)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  formSubtitle: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
    textAlign: 'center',
    opacity: 0.8,
    fontWeight: '500',
    letterSpacing: 0.2,
    marginTop: 4,
  },
  form: {
    gap: 2,
  },
  errorContainer: {
    borderRadius: 8,
    padding: 10,
    marginBottom: 8,
  },
  errorText: {
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
  },
  createAccountButton: {
    marginTop: 4,
    marginBottom: 16,
    height: 52,
    borderRadius: 16,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 6,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 1,
  },
  footerText: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
  },
  footerLink: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
    fontWeight: '600',
    fontSize: 16,
  },
  agreementContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 8,
    paddingHorizontal: 4,
    width: '100%',
  },
  introText: {
    flexShrink: 1,
    textAlign: 'center',
  },
  linksContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 1,
  },
  separatorContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
  agreementText: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
    fontSize: 13,
    fontWeight: '400',
    opacity: 0.7,
    textAlign: 'center',
  },
  agreementLink: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
    fontSize: 13,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  legalLinks: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 16,
    flexWrap: 'wrap',
  },
  legalText: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
    fontWeight: '400',
    opacity: 0.7,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  webContentContainer: {
    width: '55%',
    alignSelf: 'center',
    maxWidth: 520,
    minWidth: 400,
  },

  // Bubble Animation Styles
  bubblesContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
    pointerEvents: 'none',
    marginBottom: 50,
  },
  bubble: {
    position: 'absolute',
    borderRadius: 1000,
  },
  bubble1: {
    width: 120,
    height: 120,
    backgroundColor: 'rgba(255, 107, 107, 0.6)',
    top: '8%',
    left: '3%',
  },
  bubble2: {
    width: 120,
    height: 120,
    backgroundColor: 'rgba(78, 205, 196, 0.5)',
    top: '20%',
    right: '3%',
  },
  bubble3: {
    width: 90,
    height: 90,
    backgroundColor: 'rgba(69, 183, 209, 0.4)',
    top: '35%',
    left: '88%',
  },
  bubble4: {
    width: 110,
    height: 110,
    backgroundColor: 'rgba(150, 206, 180, 0.3)',
    top: '75%',
    left: '5%',
  },
  bubble5: {
    width: 105,
    height: 105,
    backgroundColor: 'rgba(254, 202, 87, 0.5)',
    top: '82%',
    right: '5%',
  },
  bubble6: {
    width: 75,
    height: 75,
    backgroundColor: 'rgba(255, 159, 243, 0.3)',
    top: '12%',
    left: '50%',
  },
  bubble7: {
    width: 100,
    height: 100,
    backgroundColor: 'rgba(168, 230, 207, 0.4)',
    top: '50%',
    left: '2%',
  },
  bubble8: {
    width: 97,
    height: 97,
    backgroundColor: 'rgba(255, 182, 193, 0.5)',
    top: '60%',
    right: '2%',
  },
  bubble9: {
    width: 82,
    height: 82,
    backgroundColor: 'rgba(221, 160, 221, 0.3)',
    top: '65%',
    right: '15%',
  },
  bubble10: {
    width: 67,
    height: 67,
    backgroundColor: 'rgba(152, 251, 152, 0.4)',
    top: '45%',
    left: '2%',
  },
  bubble11: {
    width: 90,
    height: 90,
    backgroundColor: 'rgba(240, 230, 140, 0.4)',
    top: '30%',
    left: '88%',
  },
  bubble12: {
    width: 60,
    height: 60,
    backgroundColor: 'rgba(255, 160, 122, 0.3)',
    top: '70%',
    left: '80%',
  },
  bubble13: {
    width: 82,
    height: 82,
    backgroundColor: 'rgba(135, 206, 235, 0.5)',
    top: '5%',
    left: '30%',
  },
  bubble14: {
    width: 75,
    height: 75,
    backgroundColor: 'rgba(216, 191, 216, 0.4)',
    top: '55%',
    right: '5%',
  },
  bubble15: {
    width: 105,
    height: 105,
    backgroundColor: 'rgba(245, 222, 179, 0.3)',
    top: '88%',
    left: '50%',
  },
  bubble16: {
    width: 52,
    height: 52,
    backgroundColor: 'rgba(230, 230, 250, 0.3)',
    top: '15%',
    left: '65%',
  },
  bubble17: {
    width: 115,
    height: 115,
    backgroundColor: 'rgba(255, 228, 225, 0.4)',
    top: '35%',
    left: '8%',
  },
  bubble18: {
    width: 67,
    height: 67,
    backgroundColor: 'rgba(240, 248, 255, 0.5)',
    top: '60%',
    right: '25%',
  },
  bubble19: {
    width: 60,
    height: 60,
    backgroundColor: 'rgba(255, 248, 220, 0.3)',
    top: '78%',
    left: '60%',
  },
  bubble20: {
    width: 97,
    height: 97,
    backgroundColor: 'rgba(253, 245, 230, 0.4)',
    top: '25%',
    right: '70%',
  },
});

export default RegisterScreen;
